import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

const servicesData = [
  { title: "Kidney Stone Removal", image: "/images/kidney_stone.webp", description: "Non-invasive laser and shock wave treatment for kidney stones." },
  { title: "Prostate Health Solutions", image: "/images/prostate-care.jpg", description: "Advanced treatment for prostate conditions, including BPH and cancer." },
  { title: "UTI Diagnosis & Treatment", image: "/images/uti-treatment.jpg", description: "Effective care for urinary tract infections and bladder health." },
  { title: "Robotic Urological Surgery", image: "/images/robotic_surgeron.jpg", description: "Precision-driven robotic-assisted surgeries for better recovery." },
  { title: "Male Fertility Treatments", image: "/images/male-infertility.jpg", description: "Expert andrology services for male reproductive health." },
  { title: "Bladder Cancer Therapies", image: "/images/bladder-cancer.jpg", description: "Comprehensive oncology treatments for bladder cancer." },
  { title: "Incontinence Solutions", image: "/images/urinary-incontinence.jpg", description: "Advanced treatments for urinary control and bladder health." },
  { title: "Uro-Flowmetry Tests", image: "/images/uroflowmetry.jpg", description: "Diagnostic tests to measure urine flow rate and bladder function." },
  { title: "Urological Cancer Care", image: "/images/uro-oncology.jpg", description: "Specialized cancer treatments for kidney, bladder, and prostate." }
];

const Services = () => {
  const [inView, setInView] = useState(false);
  const servicesRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (servicesRef.current) {
      observer.observe(servicesRef.current);
    }

    return () => {
      if (servicesRef.current) {
        observer.unobserve(servicesRef.current);
      }
    };
  }, []);

  // Updated Structured Data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "Advanced Urology Care",
    "description": "Expert urological treatments, including kidney stone removal, prostate care, robotic surgery, and bladder cancer treatments.",
    "image": "https://www.drsrikanthurologist.com/images/robotic_surgeron.jpg",
    "medicalSpecialty": "Urology",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Bangalore",
      "addressCountry": "IN"
    }
  };

  return (
    <>
      <Helmet defer={false}>
        {/* Unique SEO Title */}
        <title>Urology Care & Treatments - Kidney Stones, Prostate, Robotic Surgery</title>
        
        {/* Unique Meta Description */}
        <meta name="description" content="Discover expert urology treatments for kidney stones, prostate health, UTI, robotic surgery, and male fertility. Get personalized care from top specialists." />
        
        {/* Unique Meta Keywords */}
        <meta name="keywords" content="urology treatments, kidney stone surgery, prostate care, robotic urology, male fertility, bladder cancer therapy, urinary health, incontinence treatment" />
        
        <meta name="author" content="Dr. Srikanth Urologist" />

        {/* Open Graph for Social Media */}
        <meta property="og:title" content="Urology Treatments - Advanced Kidney, Prostate & Robotic Surgery" />
        <meta property="og:description" content="Top urology care for kidney stones, prostate issues, and robotic surgery. Expert solutions for urinary health and cancer care." />
        <meta property="og:image" content="https://www.drsrikanthurologist.com/images/prostate-care.jpg" />
        <meta property="og:type" content="website" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Expert Urology Services - Kidney Stones, Prostate & More" />
        <meta name="twitter:description" content="Advanced urological care for kidney stones, prostate health, male fertility, and bladder cancer treatments. Book your consultation now." />
        <meta name="twitter:image" content="https://www.drsrikanthurologist.com/images/uro-oncology.jpg" />

        {/* Structured Data JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <section id="services" className="flex flex-col min-h-screen bg-black text-white">
        <div className="container mx-auto p-8">
          {/* SEO Hidden Heading */}
          <h1 className="sr-only">Expert Urology Services - Kidney, Prostate, Robotic Surgery & More</h1>

          {/* Visible Section Title */}
          <h2 className="text-3xl font-bold text-blue-500 text-center mb-6">Our Urology Services</h2>

          {/* Services Grid */}
          <div ref={servicesRef} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {servicesData.map((service, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="relative group p-4 border border-gray-600 rounded-lg shadow-md bg-black text-center transition-all duration-300 transform hover:bg-teal-600 hover:border-teal-600 hover:text-white"
              >
                {/* Service Image */}
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-60 object-cover rounded-lg mb-4 transition-all duration-300 group-hover:blur-sm"
                  loading="lazy"
                />

                {/* Title Overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 group-hover:opacity-100">
                  <h3 className="text-xl font-bold text-white">{service.title}</h3>
                </div>

                {/* Service Description */}
                <p className="mt-2">{service.description}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;
